import { useMemo } from 'react';
import { ArtifactInfo, RewardEntry } from '../types';
import { extractIteration } from '../utils/artifacts';

interface IterationMap {
  videoIterations: number[];
  checkpointIterations: number[];
  nearestVideo: (iter: number) => ArtifactInfo | null;
  nearestCheckpoint: (iter: number) => ArtifactInfo | null;
  rewardAtIteration: (iter: number) => number;
}

export function useIterationMap(
  artifacts: ArtifactInfo[],
  rewardHistory: RewardEntry[]
): IterationMap {
  return useMemo(() => {
    const videoMap = new Map<number, ArtifactInfo>();
    const checkpointMap = new Map<number, ArtifactInfo>();

    for (const a of artifacts) {
      const iter = extractIteration(a.filename);
      if (iter === null) continue;
      if (a.artifact_type === 'video') videoMap.set(iter, a);
      else if (a.artifact_type === 'checkpoint') checkpointMap.set(iter, a);
    }

    const videoIterations = Array.from(videoMap.keys()).sort((a, b) => a - b);
    const checkpointIterations = Array.from(checkpointMap.keys()).sort((a, b) => a - b);

    function nearestAtOrBefore(
      sortedIters: number[],
      map: Map<number, ArtifactInfo>,
      iter: number
    ): ArtifactInfo | null {
      let best: number | null = null;
      for (const i of sortedIters) {
        if (i <= iter) best = i;
        else break;
      }
      return best !== null ? map.get(best) ?? null : null;
    }

    const nearestVideo = (iter: number) =>
      nearestAtOrBefore(videoIterations, videoMap, iter);
    const nearestCheckpoint = (iter: number) =>
      nearestAtOrBefore(checkpointIterations, checkpointMap, iter);

    const rewardAtIteration = (iter: number): number => {
      if (rewardHistory.length === 0) return 0;
      let lo = 0;
      let hi = rewardHistory.length - 1;
      if (iter <= rewardHistory[lo].iteration) return rewardHistory[lo].mean_reward;
      if (iter >= rewardHistory[hi].iteration) return rewardHistory[hi].mean_reward;

      while (lo < hi - 1) {
        const mid = Math.floor((lo + hi) / 2);
        if (rewardHistory[mid].iteration <= iter) lo = mid;
        else hi = mid;
      }

      const a = rewardHistory[lo];
      const b = rewardHistory[hi];
      const t = (iter - a.iteration) / (b.iteration - a.iteration || 1);
      return a.mean_reward + t * (b.mean_reward - a.mean_reward);
    };

    return {
      videoIterations,
      checkpointIterations,
      nearestVideo,
      nearestCheckpoint,
      rewardAtIteration,
    };
  }, [artifacts, rewardHistory]);
}
